import * as React from "react"
import {
  Appearance,
  Dimensions, Modal, Platform,
  ScrollView, StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native"
import { observer } from "mobx-react-lite"
import { color, nativeBaseTheme } from "../../theme"
import { Text } from "../text/text"
import { Actionsheet, Icon, NativeBaseProvider } from "native-base"
import { JalaIcon } from "../jala-icon/jala-icon"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { TxKeyPath } from "../../i18n"
import { useStores } from "../../models"
import { FarmStoreModel } from "../../models/app-store/farm-store/farm-store"

const HEADER: ViewStyle = {
  flexDirection: 'row',
  padding: 20,
  alignItems: "center",
}

const HEADER_TITLE: ViewStyle = {
  flexGrow: 1,
  alignItems: 'center',
}

const ITEM_MENU: ViewStyle = {
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
}

const ICON = (color: string): ViewStyle => ({
  padding: 7.5,
  borderRadius: 10,
  alignItems: 'center',
  backgroundColor: color,
  marginBottom: 8
})

const MENU_SECTION: ViewStyle = {
  paddingHorizontal: 16,
  paddingBottom: 8
}

const ROW_ICONS: ViewStyle = {
  flexDirection: 'row',
  // justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: 12
}

export interface QuickInputActionsheetProps {
  isOpen: boolean
  colorScheme?: "light" | "dark"

  onNavigate: (name: string) => void
  onClose: () => void


  /**
   * A style override to apply to the container.  Useful for margins and paddings.
   */
  style?: StyleProp<ViewStyle>
}

const MenuRow = ({ icon, name, color, section, colorScheme = Appearance.getColorScheme(), onPress }) => {
  const title = `quickInput.${name}`;

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });

  return (
    <TouchableOpacity style={ITEM_MENU} onPress={onPress}>
      <View style={ICON(section)}>
        <Icon {...iconBaseStyle} as={JalaIcon} name={icon} size="24px" color={color} />
      </View>
      <Text preset="body2" tx={title as TxKeyPath} colorScheme={colorScheme} numberOfLines={1} isTruncated />
    </TouchableOpacity>
  )
}

const FarmSection = ({ colorScheme = "light" || Appearance.getColorScheme(), onNavigate }) => (
  <View style={MENU_SECTION}>
    <Text preset="title4" tx="quickInput.farming" colorScheme={colorScheme} />
    <View style={ROW_ICONS}>
      <MenuRow
        icon="rent-circle"
        name="startCycle"
        color={color.palette.orange500}
        colorScheme={colorScheme}
        section={color.palette.orange300}
        onPress={onNavigate('Home.Cycle.CreateMultiple')}
      />
      <MenuRow
        icon="biomass"
        name="sampling"
        color={color.palette.orange500}
        colorScheme={colorScheme}
        section={color.palette.orange300}
        onPress={onNavigate("Home.Sampling.Create")}
      />
      <MenuRow
        icon="harvest"
        name="harvest"
        color={color.palette.orange500}
        colorScheme={colorScheme}
        section={color.palette.orange300}
        onPress={onNavigate("Home.Harvest.Create")}
      />
    </View>
    <View style={ROW_ICONS}>
      <MenuRow
        icon="feeds-1"
        name="closeCycle"
        color={color.palette.orange500}
        colorScheme={colorScheme}
        section={color.palette.orange300}
        onPress={onNavigate("Home.Cycle.CloseMultiple")}
      />
      <MenuRow
        icon="farm"
        name="createFarm"
        color={color.palette.blue500}
        colorScheme={colorScheme}
        section={color.palette.ocean100}
        onPress={onNavigate("Home.Farm.Create")}
      />
      <MenuRow
        icon="pond"
        name="createPond"
        color={color.palette.blue500}
        colorScheme={colorScheme}
        section={color.palette.ocean100}
        onPress={onNavigate("Home.Pond.PreCreateMultiple")}
      />
    </View>
  </View>
)

const DailySection = ({ colorScheme = Appearance.getColorScheme(), onNavigate }) => (
  <View style={MENU_SECTION}>
    <Text preset="title4" tx="quickInput.daily" colorScheme={colorScheme} />
    <View style={ROW_ICONS}>
      <MenuRow
        icon="feeds"
        name="feed"
        color={color.palette.blueAzure}
        colorScheme={colorScheme}
        section={color.palette.ocean100}
        onPress={onNavigate("Home.Feed.Create")}
      />
      <MenuRow
        icon="salinity"
        name="waterQuality"
        color={color.palette.blueAzure}
        colorScheme={colorScheme}
        section={color.palette.ocean100}
        onPress={onNavigate("Home.WaterQualityMultiMeasurement.Create")}
      />
      <MenuRow
        icon="treatments-input"
        name="treatment"
        color={color.palette.blueAzure}
        colorScheme={colorScheme}
        section={color.palette.ocean100}
        onPress={onNavigate("Home.Treatment.Create")}
      />
    </View>
    <View style={ROW_ICONS}>
      <MenuRow
        icon="feeding-tray"
        name="feedingTray"
        color={color.palette.blueAzure}
        colorScheme={colorScheme}
        section={color.palette.ocean100}
        onPress={onNavigate("Home.FeedingTrayControl.CreateMultiple")}
      />
      <View style={ITEM_MENU} />
      <View style={ITEM_MENU} />
    </View>
  </View>
)

const LabSection = ({ colorScheme = Appearance.getColorScheme(), onNavigate }) => (
  <View style={MENU_SECTION}>
    <Text preset="title4" tx="quickInput.LabTest" colorScheme={colorScheme} />
    <View style={ROW_ICONS}>
      <MenuRow
        icon="chemical"
        name="chemical"
        color={color.palette.green500}
        colorScheme={colorScheme}
        section={color.palette.green300}
        onPress={onNavigate("Home.ChemicalMultiMeasurement.Create")}
      />
      <MenuRow
        icon="microbiology"
        name="microbiology"
        color={color.palette.green500}
        colorScheme={colorScheme}
        section={color.palette.green300}
        onPress={onNavigate("Home.MicrobiologyMultiMeasurement.Create")}
      />
      <MenuRow
        icon="plankton"
        name="plankton"
        color={color.palette.green500}
        colorScheme={colorScheme}
        section={color.palette.green300}
        onPress={onNavigate("Home.PlanktonMultiMeasurement.Create")}
      />
    </View>
  </View>
)

const OthersSection = ({ colorScheme = Appearance.getColorScheme(), onNavigate }) => (
  <View style={MENU_SECTION}>
    <Text preset="title4" tx="quickInput.Others" colorScheme={colorScheme} />
    <View style={ROW_ICONS}>
      <MenuRow
        icon="diseases-input"
        name="disease"
        color={color.palette.red600}
        colorScheme={colorScheme}
        section={color.palette.red300}
        onPress={onNavigate("Home.CycleDisease.Create")}
      />
      <MenuRow
        icon="mortality"
        name="mortality"
        color={color.palette.red600}
        colorScheme={colorScheme}
        section={color.palette.red300}
        onPress={onNavigate("Home.Mortality.Create")}
      />
      <View style={ITEM_MENU} />
    </View>
  </View>
)

const actionSheetIosAwareHOC = (WrappedComponent) => (Platform.OS === 'ios') ? ((props) =>  (
  <Modal transparent visible={props.isOpen}>
    <NativeBaseProvider theme={nativeBaseTheme}>
      <WrappedComponent {...props} />
    </NativeBaseProvider>
  </Modal>
)) : WrappedComponent

export const ActionsheetIosAware = actionSheetIosAwareHOC(Actionsheet)

/**
 * Describe your component here
 */
export const QuickInputActionsheet = observer(function QuickInputActionsheet(props: QuickInputActionsheetProps) {
  const { isOpen, onClose, colorScheme = Appearance.getColorScheme(), onNavigate } = props
  const {
    appStore: { farmStoreModels = [], farmStore },
    userStore: { hasManagementInterest },
  } = useStores()
  const { activePondStores = [] } = farmStore || FarmStoreModel.create({})

  const insets = useSafeAreaInsets();

  if (Platform.OS === 'ios' && (!hasManagementInterest || !farmStoreModels.length || !activePondStores.length))
    return null;

  return (
    <ActionsheetIosAware isOpen={isOpen} onClose={onClose}>
      <Actionsheet.Content alignItems="stretch" px={0} py={0} style={{ maxHeight: Dimensions.get("window").height - insets.top - 48 }}>
        <View style={HEADER}>
          <TouchableOpacity onPress={onClose}>
            <JalaIcon name="chevron_left" size={25} color={color.palette.black} />
          </TouchableOpacity>
          <View style={HEADER_TITLE}>
            <Text tx="quickInput.quickInputTitle" preset="title3" colorScheme={colorScheme} />
          </View>
        </View>
        <ScrollView>
          <FarmSection colorScheme={colorScheme} onNavigate={onNavigate} />
          <DailySection colorScheme={colorScheme} onNavigate={onNavigate} />
          <LabSection colorScheme={colorScheme} onNavigate={onNavigate} />
          <OthersSection colorScheme={colorScheme} onNavigate={onNavigate} />
        </ScrollView>
      </Actionsheet.Content>
    </ActionsheetIosAware>
  )
})
